import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Bu uygulamaya özel VAPID public key
const VAPID_PUBLIC_KEY =
  "BBphpl-Wmy-050yLj9n6SU5g0Tv-Lfjj8dZyGdEJwB_gtFeRU3eogIer12xVRcxqtectQyHcBvMt4_sdrWW2PCo";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function usePushNotifications() {
  const { user } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const supported =
      "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setIsSupported(supported);
    if (supported) setPermission(Notification.permission);
  }, []);

  // Her açılışta aboneliği veritabanıyla SENKRONLA.
  //
  // Eskiden burada yalnızca "abonelik var mı" bakılıp bir boolean set ediliyordu;
  // veritabanına hiçbir şey yazılmıyordu. Tarayıcı aboneliği kendiliğinden
  // yenilediğinde (Chrome güncellemesi, site verisi temizliği, uzun süre
  // kullanılmama) veritabanında ÖLÜ endpoint kalıyordu. Arayüz "abonesiniz"
  // gösterdiği için kullanıcı tekrar kaydolmuyor, push ölü adrese gidiyor,
  // push servisi 410 dönmediği için temizlenmiyor ve hiçbir bildirim ulaşmıyordu.
  //
  // Artık: izin verilmişse aboneliği alıp her seferinde upsert ediyoruz;
  // abonelik düşmüşse izin zaten verili olduğu için sessizce yeniden kuruyoruz
  // (kullanıcıya soru sorulmaz).
  useEffect(() => {
    if (!isSupported || !user) return;
    let iptal = false;

    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        let sub = await reg.pushManager.getSubscription();

        if (!sub && Notification.permission === "granted") {
          const appServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: appServerKey.buffer as ArrayBuffer,
          });
        }

        if (iptal) return;
        setIsSubscribed(!!sub);
        if (!sub) return;

        const subJson = sub.toJSON();
        const keys = subJson.keys;
        if (!subJson.endpoint || !keys?.p256dh || !keys?.auth) return;

        await supabase.from("push_subscriptions" as any).upsert(
          {
            user_id: user.id,
            endpoint: subJson.endpoint,
            p256dh: keys.p256dh,
            auth: keys.auth,
            user_agent: navigator.userAgent.slice(0, 200),
          },
          { onConflict: "endpoint" },
        );
      } catch (err) {
        // Senkron başarısız olursa uygulama akışı etkilenmesin.
        console.warn("[push] abonelik senkronu basarisiz:", err);
      }
    })();

    return () => {
      iptal = true;
    };
  }, [isSupported, user]);

  const subscribe = useCallback(async () => {
    if (!isSupported || !user) return false;
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setLoading(false);
        return false;
      }

      const reg = await navigator.serviceWorker.ready;
      const appServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appServerKey.buffer as ArrayBuffer,
      });

      const subJson = subscription.toJSON();
      const keys = subJson.keys!;

      const { error } = await supabase.from("push_subscriptions" as any).upsert(
        {
          user_id: user.id,
          endpoint: subJson.endpoint!,
          p256dh: keys.p256dh!,
          auth: keys.auth!,
          user_agent: navigator.userAgent.slice(0, 200),
        },
        { onConflict: "endpoint" },
      );

      if (error) {
        console.error("Push subscription save error:", error);
        setLoading(false);
        return false;
      }

      setIsSubscribed(true);
      setLoading(false);
      return true;
    } catch (err) {
      console.error("Push subscription error:", err);
      setLoading(false);
      return false;
    }
  }, [isSupported, user]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported || !user) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await supabase.from("push_subscriptions" as any).delete().eq("endpoint", endpoint);
      }
      setIsSubscribed(false);
    } catch (err) {
      console.error("Unsubscribe error:", err);
    }
    setLoading(false);
  }, [isSupported, user]);

  return { isSupported, permission, isSubscribed, loading, subscribe, unsubscribe };
}
